import React, { useState, useEffect } from 'react';
import { HardHat, Save, Loader2, Building2, Users, Calendar, MapPin, UserCheck, Award, ChevronDown } from 'lucide-react';
import { crmApi } from '../../services/crmApi';

const TABS = [
  { id: 'company', label: 'Firma', icon: Building2, color: 'text-blue-600' },
  { id: 'training', label: 'Szkolenie', icon: HardHat, color: 'text-orange-600' },
  { id: 'logistics', label: 'Logistyka', icon: MapPin, color: 'text-emerald-600' },
] as const;

type TabId = typeof TABS[number]['id'];

const TRAINING_TYPES: Record<string, string> = {
  wstepne: 'Wstępne',
  okresowe: 'Okresowe',
  kierownicze: 'Kierownicze',
  instruktorskie: 'Instruktorskie',
  ppoz: 'Szkolenie ppoż',
};

const INDUSTRIES: Record<string, string> = {
  budownictwo: 'Budownictwo', produkcja: 'Produkcja', logistyka: 'Logistyka',
  gastronomia: 'Gastronomia', handel: 'Handel', uslugi: 'Usługi',
  biuro: 'Biuro / Administracja', zdrowie: 'Zdrowie / Opieka',
  edukacja: 'Edukacja', transport: 'Transport', inne: 'Inne',
};

interface Props {
  application: any;
  onSaved: () => void;
}

export const BHPDetailSection: React.FC<Props> = ({ application, onSaved }) => {
  const app = application;
  const [activeTab, setActiveTab] = useState<TabId>('company');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fields, setFields] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!app) return;
    const f: Record<string, any> = {};
    Object.keys(app).forEach(k => { if (k.startsWith('bhp_')) f[k] = app[k]; });
    f._notes = app.notes || '';
    f._operator_notes = app.operator_notes || '';
    setFields(f);
    setDirty(false);
  }, [app?.id]);

  const set = (key: string, value: any) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      Object.entries(fields).forEach(([k, v]) => { if (k.startsWith('bhp_')) payload[k] = v; });
      payload.notes = fields._notes;
      payload.operator_notes = fields._operator_notes;
      await crmApi.updateApplication(app.id, payload);
      setDirty(false);
      onSaved();
    } catch (e) { console.error('Save BHP failed:', e); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: number }) => (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
  const Input = ({ value, onChange, type = 'text', placeholder = '' }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) => (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
  );
  const Select = ({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Record<string, string> }) => (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent">
      <option value="">— wybierz —</option>
      {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
  const Textarea = ({ value, onChange, rows = 3, placeholder = '' }: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) => (
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" />
  );

  const participantCount = parseInt(fields.bhp_participant_count) || 0;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-6 py-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardHat size={22} className="text-orange-600" />
            <h3 className="text-lg font-bold text-gray-900">Szkolenie BHP — szczegóły</h3>
          </div>
          <button onClick={handleSave} disabled={!dirty || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${dirty ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-sm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Zapisz
          </button>
        </div>
        {participantCount > 0 && (
          <div className="mt-2 flex gap-4 text-sm">
            <div className="bg-white/70 rounded-lg px-3 py-1.5">
              <span className="text-gray-500">Uczestników: </span>
              <span className="font-bold text-orange-700">{participantCount}</span>
            </div>
            {fields.bhp_training_type && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Typ: </span>
                <span className="font-bold text-orange-700">{TRAINING_TYPES[fields.bhp_training_type] || fields.bhp_training_type}</span>
              </div>
            )}
            {fields.bhp_scheduled_date && (
              <div className="bg-white/70 rounded-lg px-3 py-1.5">
                <span className="text-gray-500">Zaplanowane: </span>
                <span className="font-bold text-orange-700">{new Date(fields.bhp_scheduled_date).toLocaleDateString('pl-PL')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex border-b bg-gray-50 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${activeTab === tab.id ? `${tab.color} border-current bg-white` : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      <div className="p-6">
        {activeTab === 'company' && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nazwa firmy"><Input value={fields.bhp_company_name} onChange={v => set('bhp_company_name', v)} /></Field>
            <Field label="NIP"><Input value={fields.bhp_company_nip} onChange={v => set('bhp_company_nip', v)} /></Field>
            <Field label="Adres" span={2}><Input value={fields.bhp_company_address} onChange={v => set('bhp_company_address', v)} /></Field>
            <Field label="Branża"><Select value={fields.bhp_company_industry} onChange={v => set('bhp_company_industry', v)} options={INDUSTRIES} /></Field>
            <Field label="Osoba kontaktowa"><Input value={fields.bhp_contact_position} onChange={v => set('bhp_contact_position', v)} placeholder="Stanowisko" /></Field>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Typ szkolenia"><Select value={fields.bhp_training_type} onChange={v => set('bhp_training_type', v)} options={TRAINING_TYPES} /></Field>
              <Field label="Liczba uczestników"><Input value={fields.bhp_participant_count} onChange={v => set('bhp_participant_count', v)} type="number" /></Field>
            </div>
            <Field label="Przypisany trener">
              <Input value={fields.bhp_trainer_assigned} onChange={v => set('bhp_trainer_assigned', v)} placeholder="Imię i nazwisko trenera BHP" />
            </Field>
            <Field label="Notatki operatorskie" span={2}>
              <Textarea value={fields._operator_notes} onChange={v => set('_operator_notes', v)} rows={3} placeholder="Uwagi operatora..." />
            </Field>
          </div>
        )}

        {activeTab === 'logistics' && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Lokalizacja szkolenia"><Input value={fields.bhp_training_location} onChange={v => set('bhp_training_location', v)} placeholder="Adres lub 'online'" /></Field>
            <Field label="Preferowana data"><Input value={fields.bhp_preferred_date} onChange={v => set('bhp_preferred_date', v)} type="date" /></Field>
            <Field label="Zaplanowana data"><Input value={fields.bhp_scheduled_date} onChange={v => set('bhp_scheduled_date', v)} type="date" /></Field>
            <div />
            <Field label="Notatki klienta" span={2}>
              <Textarea value={fields._notes} onChange={v => set('_notes', v)} rows={3} placeholder="Notatki z formularza..." />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
};
